/**
 * GB English collection/search grids skip uk-blocked cards inside Shopify
 * pagination, which leaves hollow pages. This fills a sparse non-last page
 * from later section-rendered pages. Numbered pagination (prev / page 1)
 * always stays visible when there is more than one page — including the last
 * remainder page. No-ops when the page is already full (ES/PT, or GB after
 * the UK catalog excludes blocked products).
 */
(function () {
  const loc = window.LaceLocalization || {};
  const country = String(loc.country || '').toUpperCase();
  const locale = String(loc.locale || '').toLowerCase();
  if (country !== 'GB' && country !== 'UK') return;
  if (locale && locale !== 'en') return;

  const MAX_AUTO_PAGES = 20;

  function visibleItems(grid) {
    return Array.from(grid.children).filter((el) => {
      if (el.tagName !== 'LI') return false;
      if (el.hasAttribute('hidden')) return false;
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden';
    });
  }

  function itemKey(li) {
    const link = li.querySelector('a[href*="/products/"], a.full-unstyled-link');
    return link ? link.getAttribute('href') : li.innerHTML.slice(0, 120);
  }

  function currentQuery() {
    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.delete('section_id');
    return params;
  }

  function buildUrl(grid, page) {
    const params = currentQuery();
    params.set('section_id', grid.dataset.laceSection);
    params.set('page', String(page));
    return `${window.location.pathname}?${params.toString()}`;
  }

  function extractItems(html, gridId) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const remote =
      doc.getElementById(gridId) ||
      doc.querySelector('[data-lace-gb-fill="true"]') ||
      doc.querySelector('#product-grid ul.product-grid, ul.product-grid');
    if (!remote) return [];
    return Array.from(remote.querySelectorAll(':scope > li'));
  }

  function showEmpty(grid) {
    if (document.getElementById('product-grid-uk-empty')) return;
    const tpl = document.getElementById('LaceUkEmptyTemplate');
    if (!tpl) return;
    const node = tpl.content.cloneNode(true);
    grid.replaceChildren();
    grid.parentElement.appendChild(node);
  }

  function setPaginationVisible(grid, visible) {
    const root = grid.closest('#ProductGridContainer') || grid.parentElement;
    if (!root) return;
    root.querySelectorAll('.pagination-wrapper').forEach((el) => {
      el.hidden = !visible;
      el.style.display = visible ? '' : 'none';
    });
  }

  function ensureLoadMore(grid) {
    let btn = grid.parentElement.querySelector('[data-lace-gb-load-more]');
    if (btn) return btn;
    btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'button button--secondary';
    btn.dataset.laceGbLoadMore = 'true';
    btn.textContent = grid.dataset.laceLoadMoreLabel || 'Show more';
    btn.style.display = 'none';
    btn.style.margin = '2rem auto';
    const wrap = document.createElement('div');
    wrap.className = 'center';
    wrap.appendChild(btn);
    grid.after(wrap);
    btn.addEventListener('click', () => fill(grid, { auto: false, targetExtra: Number(grid.dataset.lacePerPage || 16) }));
    return btn;
  }

  async function fetchPage(grid, page) {
    const response = await fetch(buildUrl(grid, page));
    if (!response.ok) return [];
    return extractItems(await response.text(), grid.id);
  }

  async function fill(grid, opts) {
    if (grid.dataset.laceFillBusy === 'true') return;
    const perPage = Number(grid.dataset.lacePerPage || 16);
    const lastShopifyPage = Number(grid.dataset.lacePages || 1);
    let nextPage = Number(grid.dataset.laceNextPage || Number(grid.dataset.lacePage || 1) + 1);
    const target = opts.auto ? perPage : visibleItems(grid).length + (opts.targetExtra || perPage);

    grid.dataset.laceFillBusy = 'true';
    const btn = ensureLoadMore(grid);
    btn.disabled = true;

    const seen = new Set(visibleItems(grid).map(itemKey));
    let fetched = 0;

    try {
      while (visibleItems(grid).length < target && nextPage <= lastShopifyPage && fetched < MAX_AUTO_PAGES) {
        const items = await fetchPage(grid, nextPage);
        nextPage += 1;
        fetched += 1;
        const available = [];
        const soldOut = [];
        items.forEach((li) => {
          const key = itemKey(li);
          if (!key || seen.has(key)) return;
          seen.add(key);
          if (li.getAttribute('data-available') === 'false') soldOut.push(li);
          else available.push(li);
        });
        available.forEach((li) => grid.appendChild(li));
        soldOut.forEach((li) => grid.appendChild(li));
        if (!items.length) break;
      }
    } catch (err) {
      console.warn('lace-uk-grid-fill', err);
    }

    Array.from(grid.querySelectorAll(':scope > li[data-available="false"]')).forEach((li) => {
      grid.appendChild(li);
    });
    grid.dataset.laceNextPage = String(nextPage);
    grid.dataset.laceFillBusy = 'false';
    btn.disabled = false;

    const visible = visibleItems(grid).length;
    const more = nextPage <= lastShopifyPage;
    const sparse = visible < perPage && lastShopifyPage > 1;
    const currentPage = Number(grid.dataset.lacePage || 1);

    setPaginationVisible(grid, more || currentPage > 1);

    if (visible === 0 && !more) {
      showEmpty(grid);
      btn.style.display = 'none';
      return;
    }

    if (more && (sparse || opts.auto === false)) {
      btn.style.display = '';
    } else {
      btn.style.display = 'none';
    }
    grid.dispatchEvent(new CustomEvent('lace:grid-ready', { bubbles: true }));
  }

  function bind(grid) {
    if (!grid || grid.dataset.laceFillBound === 'true') return;
    if (grid.dataset.laceGbFill !== 'true') return;
    grid.dataset.laceFillBound = 'true';
    const perPage = Number(grid.dataset.lacePerPage || 16);
    const pages = Number(grid.dataset.lacePages || 1);
    const currentPage = Number(grid.dataset.lacePage || 1);
    if (currentPage > 1) {
      setPaginationVisible(grid, true);
    }
    if (visibleItems(grid).length >= perPage || pages <= 1 || currentPage >= pages) {
      grid.dispatchEvent(new CustomEvent('lace:grid-ready', { bubbles: true }));
      return;
    }
    fill(grid, { auto: true });
  }

  function scan(root) {
    (root || document).querySelectorAll('[data-lace-gb-fill="true"]').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan());
  } else {
    scan();
  }

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      m.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches?.('[data-lace-gb-fill="true"]')) bind(node);
        else scan(node);
      });
    });
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
